'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { mergeSalonNameIntoSession } from '@/lib/tenant/session';

import { QueryKeys } from '@/constants/query-keys';

export interface Tenant {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  /** Salon login password stored on tenants (used for deep links; treat as sensitive). */
  password: string | null;
  salon_name: string;
  salon_slug: string | null;
  logo_text: string | null;
  hero_image: string | null;
  domain: string | null;
  website_title: string | null;
  tour_taken: boolean | null;
}

type UpdateTenantPayload = Partial<
  Omit<Tenant, 'id'>
>;

export function useTenant() {
  return useQuery<Tenant>({
    queryKey: [QueryKeys.TENANT],
    queryFn: async () => {
      const res = await fetch('/api/tenant/profile');
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Failed to fetch tenant profile');
      }
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();

  return useMutation<Tenant, Error, UpdateTenantPayload>({
    mutationFn: async (payload) => {
      const res = await fetch('/api/tenant/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.error || 'Failed to update tenant profile');
      }

      return json;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([QueryKeys.TENANT], data);
      if (data.salon_name?.trim()) {
        mergeSalonNameIntoSession(data.salon_name.trim());
      }
      toast.success('Changes saved successfully');
    },
    onError: (error) => {
      toast.error('Failed to save changes', {
        description: error.message || 'Please try again.',
      });
    },
  });
}
